import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Modal, theme, useToast } from '@arnold/common';
import { useSendReactionMutation } from '../../generated/hooks';
import { useTranslation } from 'react-i18next';
import { useIsMobileView } from './ResponsiveSwitcher';

type ReactionModalProps = {
  accessToken: string;
  channelId: string;
  messageId?: string;
  onClose: () => void;
  reload?: () => void;
  dataCy?: string;
};

const ReactionModal = ({ accessToken, channelId, messageId, onClose, reload, dataCy }: ReactionModalProps) => {
  const isMobile = useIsMobileView();
  const [text, setText] = useState('');
  const { addToast } = useToast();
  const [sendReaction] = useSendReactionMutation({
    refetchQueries: ['ReportSequenceRespondentDetailQuery'],
    onCompleted: () => {
      addToast(t('reactionModalSuccess'));
      reload?.();
    },
    onError: (e) => {
      addToast(t('reactionModalError'));
    },
  });
  const { t } = useTranslation('Report');
  const maxLength = 500;

  return (
    <Modal
      title={t('reactionModalTitle')}
      content={
        <ReactionModalContentWrapper>
          <ReactionModalContentText>{t('reactionModalText')}</ReactionModalContentText>
          <ReactionModalContentTextAreaLabel>{t('reactionModalTextAreaLabel')}</ReactionModalContentTextAreaLabel>
          <TextAreaWrapper>
            <ReactionModalContentTextArea
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={maxLength}
              data-cy="reaction-modal-input"
            />
            <CharacterCounter>
              {text.length} / {maxLength}
            </CharacterCounter>
          </TextAreaWrapper>
        </ReactionModalContentWrapper>
      }
      onClose={onClose}
      show
      onHide={onClose}
      onSubmit={async () => {
        if (!text.trim()) return;
        await sendReaction({ variables: { accessToken, channelId, messageId, text } });
        onClose();
      }}
      buttons={{
        cancel: { title: t('closeModalButton') },
        submit: { title: t('submitModalButton') },
      }}
      verticalButtons={isMobile}
      dataCy={dataCy}
    />
  );
};

const ReactionModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
`;

const ReactionModalContentText = styled.div`
  font-size: ${theme.typography.body.medium.regular.fontSize};
  font-weight: ${theme.typography.body.medium.regular.fontWeight};
  line-height: ${theme.typography.body.medium.regular.lineHeight};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.f};
`;

const ReactionModalContentTextAreaLabel = styled.div`
  position: relative;
  left: 16px;
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.regular.fontWeight};
  line-height: ${theme.typography.body.small.regular.lineHeight};
  color: ${theme.colors.text.secondary};
`;

const TextAreaWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ReactionModalContentTextArea = styled.textarea`
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.spacing.d};
  resize: none;
  padding: ${theme.spacing.d};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  font-weight: ${theme.typography.body.medium.regular.fontWeight};
  line-height: ${theme.typography.body.medium.regular.lineHeight};
  color: ${theme.colors.text.secondary};
`;

const CharacterCounter = styled.div`
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.regular.fontWeight};
  line-height: ${theme.typography.body.small.regular.lineHeight};
  color: ${theme.colors.text.secondary};
  text-align: right;
  margin-top: ${theme.spacing.b};
`;

export { ReactionModal };
